/** Supabase auth client with fresh getSession(), dev bypass, and env validation. Per D-22/D-23/D-25, SPA-06. */

import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log("[auth] env check:", { supabaseUrl: supabaseUrl ? supabaseUrl.slice(0, 30) + "..." : "MISSING", anonKey: supabaseAnonKey ? "SET" : "MISSING" });

// D-25: Fail-fast on missing env vars in production
if (!import.meta.env.DEV && (!supabaseUrl || !supabaseAnonKey)) {
  throw new Error("[auth] Missing required env vars: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY");
}

export const supabase = createClient(
  supabaseUrl || "http://localhost:54321",
  supabaseAnonKey || "dev-anon-key"
);

/**
 * Dev/test bypass: when DEV mode AND no real Supabase credentials are configured,
 * skip auth checks so MSW-served surfaces render without redirects.
 * T-11-02-06: gated by import.meta.env.DEV which is false in production builds.
 */
export function isDevBypass(): boolean {
  const bypass = import.meta.env.DEV && (!supabaseUrl || supabaseUrl === "http://localhost:54321");
  console.log("[auth] isDevBypass:", bypass, { DEV: import.meta.env.DEV, supabaseUrl: !!supabaseUrl });
  return bypass;
}

/**
 * Stable fake user id used ONLY in dev/test bypass (no real Supabase session, so
 * supabase.auth.getUser() resolves to null). Lets surfaces that key off "who am I" —
 * e.g. the People "You" card — resolve a current user against MSW fixtures. Gated out
 * of production by isDevBypass()'s import.meta.env.DEV check.
 */
export const DEV_BYPASS_USER_ID = "00000000-0000-0000-0000-0000000000de";

let cachedAccessToken: string | null = null;

/** Set the cached access token. Called from App auth gate and onAuthStateChange. */
export function setAccessToken(token: string | null) {
  cachedAccessToken = token;
}

/**
 * Returns the cached access token (set by the App auth gate).
 * Falls back to getSession() only if cache is empty (should not happen in normal flow).
 */
export async function getAccessToken(): Promise<string | null> {
  if (isDevBypass()) return null;
  if (cachedAccessToken) return cachedAccessToken;
  const { data: { session } } = await supabase.auth.getSession();
  cachedAccessToken = session?.access_token ?? null;
  return cachedAccessToken;
}

/** Subscribe to auth state changes (sign-in, sign-out, token refresh). */
export function onAuthChange(
  callback: (event: string, session: unknown) => void
) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return subscription;
}
